import { z } from 'zod';

/**
 * Public config, validated once at startup.
 *
 * These are ordinary Vite variables: `import.meta.env` is inlined at build time, so the URLs
 * are baked into the bundle and one image serves exactly one environment. Jenkins passes them
 * per branch as Docker build args (see `apps/admin/Dockerfile` and `docs/DEPLOYMENT.md`);
 * locally they come from `.env`.
 *
 * `VITE_API_BASE_URL` is deliberately NOT a strict URL here (unlike the portal): admin's
 * `.env.example` sets it to the relative `/api` so the Vite dev proxy keeps the auth cookie
 * first-party. Both forms are valid input — `src/apiClient.ts` normalises them.
 */

/** Matches the local sash-admin-api default; keeps a bare `pnpm dev` working with no `.env`. */
const DEFAULT_API_BASE_URL = 'http://localhost:8080';

const envSchema = z.object({
  VITE_API_BASE_URL: z.string().min(1).default(DEFAULT_API_BASE_URL),
  // Unset in dev: the app then falls back to the page origin so the Vite proxy forwards
  // gRPC-web service paths to the backend. See `config/grpc.ts`.
  VITE_GRPC_URL: z.string().min(1).optional(),
});

export type Env = z.infer<typeof envSchema>;

/**
 * A variable left unset in the Docker build arrives as an empty string, not as `undefined` —
 * which would satisfy `min(1)` nowhere but would defeat `.default()` everywhere. Normalise
 * first so an unset variable falls through to the schema's default instead of failing parse.
 */
function readEnv(key: 'VITE_API_BASE_URL' | 'VITE_GRPC_URL'): string | undefined {
  const value = import.meta.env[key] as string | undefined;
  const trimmed = value?.trim();
  return trimmed === '' ? undefined : trimmed;
}

export const env: Env = envSchema.parse({
  VITE_API_BASE_URL: readEnv('VITE_API_BASE_URL'),
  VITE_GRPC_URL: readEnv('VITE_GRPC_URL'),
});
